/**
 * File Validation Constants
 * 
 * Centralized file type validation constants for consistent validation across the application.
 * These constants define allowed file types and corresponding error messages for different upload sections.
 */

// Common file type arrays
export const PDF_ONLY_TYPES = ['pdf'] as const;

export const PDF_AND_IMAGES_TYPES = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'tiff',
  'tif',
  'webp',
  'svg',
  'heic',
  'heif'
] as const;

export const PDF_DOCUMENTS_AND_IMAGES_TYPES = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'csv',
  'odt',
  'ods',
  'odp',
  'wps',
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'tiff',
  'tif',
  'webp',
  'svg',
  'heic',
  'heif'
] as const;

/** Dot-prefixed extensions for `<input accept="...">` (matches {@link PDF_DOCUMENTS_AND_IMAGES_TYPES}). */
export const PDF_DOCUMENTS_AND_IMAGES_ACCEPT =
  PDF_DOCUMENTS_AND_IMAGES_TYPES.map((t) => `.${t.toLowerCase()}`);

export const DIELINE_DOCUMENTS_TYPES = [
  'pdf',
  'ai',
  'eps',
  'svg'
] as const;

// Toast messages
export const PDF_ONLY_TOAST_MESSAGE = "Upload failed: Invalid file type. Only PDF files are allowed";

export const PDF_AND_IMAGES_TOAST_MESSAGE = "Upload failed: Invalid file type. Only pdf, jpg, jpeg, png, bmp, tiff, tif, webp, svg, heic, heif files are allowed";

export const PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE = "Upload failed: Invalid file type. Only pdf, doc, docx, xls, xlsx, ppt, pptx, txt, rtf, csv, odt, ods, odp, wps, jpg, jpeg, png, bmp, tiff, tif, webp, svg, heic, heif filetypes are allowed";

export const FILE_SIZE_EXCEEDED_40MB_MESSAGE =
  "Upload failed: File size should not exceed 40 MB.";

export const DIELINE_DOCUMENTS_TOAST_MESSAGE = "Upload failed: Invalid file type. Only pdf, ai, eps, svg files are allowed";

// Section-specific validation configs
export const FILE_VALIDATION_CONFIG = {
  // Compliance Documents
  COMPLIANCE_DOCUMENTS: {
    allowedTypes: PDF_ONLY_TYPES,
    toastMessage: PDF_ONLY_TOAST_MESSAGE,
  },
  
  // MVA Documents
  MVA_DOCUMENTS: {
    allowedTypes: PDF_ONLY_TYPES,
    toastMessage: PDF_ONLY_TOAST_MESSAGE,
  },
  
  // NDA Documents
  NDA_DOCUMENTS: {
    allowedTypes: PDF_ONLY_TYPES,
    toastMessage: PDF_ONLY_TOAST_MESSAGE,
  },
  
  // NFP Documents
  NFP_DOCUMENTS: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Ingredients List / Raw Materials
  INGREDIENTS_LIST: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Allergens & Sensitivities
  ALLERGENS_AND_SENSITIVITIES: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Nutrition
  NUTRITION: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Food Safety - Vendor Q&A
  FOOD_SAFETY_VENDOR_QA: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Food Safety - Other Attachments
  FOOD_SAFETY_OTHER_ATTACHMENTS: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Packaging & Labeling - Attachments
  PACKAGING_ATTACHMENTS: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },

  // Artwork — additional attachments (Active Projects / Projects in Artwork)
  ARTWORK_ADDITIONAL_ATTACHMENTS: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
    sizeExceededToastMessage: FILE_SIZE_EXCEEDED_40MB_MESSAGE,
  },
  
  // Packaging & Labeling - Dieline Documents
  PACKAGING_DIELINE_DOCUMENTS: {
    allowedTypes: DIELINE_DOCUMENTS_TYPES,
    toastMessage: DIELINE_DOCUMENTS_TOAST_MESSAGE,
  },
  
  // Vendor Library
  VENDOR_LIBRARY: {
    allowedTypes: PDF_DOCUMENTS_AND_IMAGES_TYPES,
    toastMessage: PDF_DOCUMENTS_AND_IMAGES_TOAST_MESSAGE,
  },
  
  // Retail Packaging Images (Main Details)
  RETAIL_PACKAGING_IMAGES: {
    allowedTypes: PDF_AND_IMAGES_TYPES,
    toastMessage: PDF_AND_IMAGES_TOAST_MESSAGE,
  },
} as const;

